import authConfig, { type AuthMode } from '@/config/auth.config'

export type StorageScope = 'session' | 'persistent'

export interface TenantInfo {
  tenant_id: string | null
  tenant_name: string | null
  roles: string[]
  is_global: boolean
}

export interface AuthUserInfo {
  id: string
  username: string
  display_name: string
  role: string | null
  roles?: string[]
  permissions: string[]
  available_tenants?: TenantInfo[]
}

export interface StoredAuth {
  accessToken: string
  refreshToken: string
  expiresAt: number
  refreshExpiresAt: number
  user?: AuthUserInfo
  scope: StorageScope
  mode: AuthMode
  currentTenantId?: string | null
}

const KEY_SCOPE = 'auth_storage_scope'
const KEY_MODE = 'auth_mode'
const KEY_ACCESS = 'auth_access_token'
const KEY_REFRESH = 'auth_refresh_token'
const KEY_EXPIRES_AT = 'auth_expires_at'
const KEY_REFRESH_EXPIRES_AT = 'auth_refresh_expires_at'
const KEY_USER = 'auth_user'
const KEY_CURRENT_TENANT = 'auth_current_tenant_id'

/**
 * 登录态统一存 localStorage（跨标签页/新窗口共享）。
 * "记住我"不再切换存储介质（sessionStorage 的标签页隔离会导致新标签页重新登录），
 * 而是只影响 refresh_token 时长（后端按 remember_me 签发 7 天 / 24 小时）。
 */
function getStorage(_scope: StorageScope): Storage {
  return localStorage
}

export function setStorageScope(scope: StorageScope) {
  localStorage.setItem(KEY_SCOPE, scope)
}

export function getStorageScope(): StorageScope {
  const scope = localStorage.getItem(KEY_SCOPE) as StorageScope | null
  return scope || (authConfig.mode === 'app' ? 'persistent' : 'session')
}

export function setAuthMode(mode: AuthMode) {
  localStorage.setItem(KEY_MODE, mode)
}

export function getAuthMode(): AuthMode {
  const mode = localStorage.getItem(KEY_MODE) as AuthMode | null
  return mode || authConfig.mode
}

export function saveTokens(data: StoredAuth) {
  const storage = getStorage(data.scope)

  // 清理历史 sessionStorage 残留（旧版按介质存储），避免脏数据
  const legacySession = sessionStorage
  legacySession.removeItem(KEY_ACCESS)
  legacySession.removeItem(KEY_REFRESH)
  legacySession.removeItem(KEY_EXPIRES_AT)
  legacySession.removeItem(KEY_REFRESH_EXPIRES_AT)
  legacySession.removeItem(KEY_USER)

  setStorageScope(data.scope)
  setAuthMode(data.mode)

  storage.setItem(KEY_ACCESS, data.accessToken)
  storage.setItem(KEY_REFRESH, data.refreshToken)
  storage.setItem(KEY_EXPIRES_AT, String(data.expiresAt))
  storage.setItem(KEY_REFRESH_EXPIRES_AT, String(data.refreshExpiresAt))
  if (data.user) {
    storage.setItem(KEY_USER, JSON.stringify(data.user))
  }
  if (data.currentTenantId != null) {
    storage.setItem(KEY_CURRENT_TENANT, data.currentTenantId)
  }
}

export function loadTokens(): StoredAuth | null {
  const scope = getStorageScope()
  const storage = getStorage(scope)

  // 兼容旧版：若 localStorage 无 token 但 sessionStorage 有（旧会话级登录），迁移一份到 localStorage 后读取
  if (!storage.getItem(KEY_ACCESS) && !storage.getItem(KEY_REFRESH)) {
    const legacy = sessionStorage
    const oldAccess = legacy.getItem(KEY_ACCESS)
    const oldRefresh = legacy.getItem(KEY_REFRESH)
    if (oldAccess || oldRefresh) {
      const legacyPairs: Array<[string, string | null]> = [
        [KEY_ACCESS, oldAccess],
        [KEY_REFRESH, oldRefresh],
        [KEY_EXPIRES_AT, legacy.getItem(KEY_EXPIRES_AT)],
        [KEY_REFRESH_EXPIRES_AT, legacy.getItem(KEY_REFRESH_EXPIRES_AT)],
        [KEY_USER, legacy.getItem(KEY_USER)],
        [KEY_CURRENT_TENANT, legacy.getItem(KEY_CURRENT_TENANT)]
      ]
      legacyPairs.forEach(([k, v]) => {
        if (v != null) localStorage.setItem(k, v)
      })
    }
  }

  const accessToken = storage.getItem(KEY_ACCESS) || ''
  const refreshToken = storage.getItem(KEY_REFRESH) || ''
  const expiresAt = Number(storage.getItem(KEY_EXPIRES_AT) || 0)
  const refreshExpiresAt = Number(storage.getItem(KEY_REFRESH_EXPIRES_AT) || 0)
  const userRaw = storage.getItem(KEY_USER)
  const user = userRaw ? (JSON.parse(userRaw) as AuthUserInfo) : undefined
  const mode = getAuthMode()
  const currentTenantIdRaw = storage.getItem(KEY_CURRENT_TENANT)

  if (!accessToken && !refreshToken) return null

  return {
    accessToken,
    refreshToken,
    expiresAt,
    refreshExpiresAt,
    user,
    scope,
    mode,
    currentTenantId: currentTenantIdRaw || null
  }
}

export function clearTokens() {
  const scopes: StorageScope[] = ['session', 'persistent']
  scopes.forEach((scope) => {
    const storage = getStorage(scope)
    storage.removeItem(KEY_ACCESS)
    storage.removeItem(KEY_REFRESH)
    storage.removeItem(KEY_EXPIRES_AT)
    storage.removeItem(KEY_REFRESH_EXPIRES_AT)
    storage.removeItem(KEY_USER)
    storage.removeItem(KEY_CURRENT_TENANT)
  })
}

export function isAccessTokenExpired(expiresAt: number): boolean {
  return !expiresAt || Date.now() >= expiresAt
}

export function isRefreshTokenExpired(refreshExpiresAt: number): boolean {
  return !refreshExpiresAt || Date.now() >= refreshExpiresAt
}

export function shouldRefreshSoon(expiresAt: number, thresholdSeconds: number): boolean {
  if (!expiresAt) return true
  const thresholdMs = thresholdSeconds * 1000
  return expiresAt - Date.now() <= thresholdMs
}
